import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { Plus, Trash2, Pencil, RotateCw } from "lucide-react";

import {
  getHomeViews,
  deleteHomeView as deleteHomeViewApi,
  saveHomeViewOrder,
} from "../../../../api/home";
import { usePortalTarget, feedViewIcon } from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const HomeSidebarPortal = () => {
  const portalTarget = usePortalTarget("home-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const navigate = useNavigate();

  const [homeViews, setHomeViews] = useState([]);
  const [homeViewOrder, setHomeViewOrder] = useState([]);
  const [activeHomeViewId, setActiveHomeViewId] = useState(null);
  const [pendingHomeDeleteId, setPendingHomeDeleteId] = useState(null);

  // Clear active state when another section becomes active
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "home") {
        setActiveHomeViewId(null);
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  // Drag state
  const [homeDragIdx, setHomeDragIdx] = useState(null);
  const [homeDragOverIdx, setHomeDragOverIdx] = useState(null);

  /* ── Load data ── */
  const loadHomeData = useCallback(async () => {
    try {
      const res = await getHomeViews();
      const d = res?.data || {};
      const fetchedViews = d.views || [];
      setHomeViews(fetchedViews);
      if (d.view_order) setHomeViewOrder(d.view_order);

      if (!activeHomeViewId && fetchedViews.length > 0) {
        const defaultView = fetchedViews.find((v) => v.is_default);
        setActiveHomeViewId(
          defaultView ? defaultView.document_id : fetchedViews[0].document_id,
        );
      }
    } catch (e) {
      console.error("Error loading home views:", e);
    }
  }, [activeHomeViewId]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  useEffect(() => {
    const handler = () => loadHomeData();
    window.addEventListener("sidebar:refresh-home-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-home-views", handler);
  }, [loadHomeData]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.viewId) setActiveHomeViewId(e.detail.viewId);
    };
    window.addEventListener("home:active-view-changed", handler);
    return () =>
      window.removeEventListener("home:active-view-changed", handler);
  }, []);

  /* ── Breadcrumb: emit active sub-item ── */
  const emitHomeBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/home") return;
    const activeView = homeViews.find(
      (v) => v.document_id === activeHomeViewId,
    );
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/home",
          subLabel: activeView?.name || null,
          subIcon: activeView?.icon || null,
          hasAdd: true,
          addKind: "home",
          addLabel: "Add home view",
        },
      }),
    );
  }, [activeHomeViewId, homeViews, currentAppPage]);

  useEffect(() => {
    emitHomeBreadcrumb();
  }, [emitHomeBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/home") emitHomeBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitHomeBreadcrumb]);

  /* ── Breadcrumb: listen for add action ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/home") {
        if (currentAppPage !== "/home") navigate("/home");
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:home-open-create-modal"),
          );
        }, 50);
      }
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate]);

  /* ── Ordered list ── */
  const orderedHomeItems = useMemo(() => {
    const itemMap = new Map();
    homeViews.forEach((v) => itemMap.set(v.document_id, v));

    if (homeViewOrder.length > 0) {
      const result = [];
      const used = new Set();
      homeViewOrder.forEach((id) => {
        if (itemMap.has(id)) {
          result.push(itemMap.get(id));
          used.add(id);
        }
      });
      itemMap.forEach((item, id) => {
        if (!used.has(id)) result.push(item);
      });
      return result;
    }
    return [...itemMap.values()];
  }, [homeViews, homeViewOrder]);

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/home",
      orderedHomeItems.map((v) => ({
        id: v.document_id,
        label: v.name,
        keywords: [],
        icon: feedViewIcon(v.icon),
        iconColor: v.color || "rgba(14,165,233,0.70)",
        event: {
          navigateTo: "/home",
          name: "home:switch-view",
          detail: { viewId: v.document_id },
        },
      })),
    );
    return () => unregister("/home");
  }, [orderedHomeItems, register, unregister]);

  /* ── Navigation ── */
  const goToHome = () => {
    if (currentAppPage !== "/home") {
      navigate("/home");
    }
  };

  const handleHomeViewClick = (view) => {
    setActiveHomeViewId(view.document_id);
    goToHome();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "home" },
      }),
    );
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("home:switch-view", {
          detail: { viewId: view.document_id },
        }),
      );
    }, 50);
  };

  /* ── Add / Edit / Delete — dispatch events to MainHome ── */
  const handleAddHomeView = () => {
    goToHome();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("sidebar:home-open-create-modal"));
    }, 50);
  };

  const handleEditHomeView = (e, view) => {
    e.stopPropagation();
    goToHome();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:home-open-edit-modal", {
          detail: { view },
        }),
      );
    }, 50);
  };

  const handleDeleteHomeView = async (e, view) => {
    e.stopPropagation();
    if (view.is_default) return;
    try {
      await deleteHomeViewApi({ document_id: view.document_id });
      if (activeHomeViewId === view.document_id) {
        const defaultView = homeViews.find((v) => v.is_default);
        if (defaultView) {
          setActiveHomeViewId(defaultView.document_id);
          window.dispatchEvent(
            new CustomEvent("home:switch-view", {
              detail: { viewId: defaultView.document_id },
            }),
          );
        }
      }
      loadHomeData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-home-views"));
    } catch (err) {
      console.error("Delete home view failed:", err);
    }
  };

  /* ── Context menu registrations ── */
  const confirmDeleteHomeView = async (documentId) => {
    const view = homeViews.find((v) => v.document_id === documentId);
    if (view?.is_default) return;
    try {
      await deleteHomeViewApi({ document_id: documentId });
      if (activeHomeViewId === documentId) {
        const defaultView = homeViews.find((v) => v.is_default);
        if (defaultView) {
          setActiveHomeViewId(defaultView.document_id);
          window.dispatchEvent(
            new CustomEvent("home:switch-view", {
              detail: { viewId: defaultView.document_id },
            }),
          );
        }
      }
      loadHomeData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-home-views"));
    } catch (err) {
      console.error("Delete home view failed:", err);
    } finally {
      setPendingHomeDeleteId(null);
    }
  };

  useContextMenu("home-view", [
    {
      label: "Reset to Default",
      icon: RotateCw,
      hidden: (payload) => !payload?.is_default,
      action: () => {
        window.dispatchEvent(new CustomEvent("home:reset-default-view"));
      },
    },
    {
      label: "Edit View",
      icon: Pencil,
      action: (payload) => {
        goToHome();
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:home-open-edit-modal", {
              detail: { view: payload },
            }),
          );
        }, 50);
      },
    },
    {
      label: "Delete View",
      icon: Trash2,
      variant: "danger",
      hidden: (payload) => payload?.is_default,
      action: (payload) => {
        setPendingHomeDeleteId(payload.document_id);
      },
    },
  ]);

  /* ── Drag and drop ── */
  const handleHomeDragStart = (e, idx) => {
    setHomeDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget) e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
  };

  const handleHomeDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== homeDragOverIdx) setHomeDragOverIdx(idx);
  };

  const handleHomeDragLeave = () => setHomeDragOverIdx(null);

  const handleHomeDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (homeDragIdx === null || homeDragIdx === dropIdx) {
      setHomeDragIdx(null);
      setHomeDragOverIdx(null);
      return;
    }

    const items = [...orderedHomeItems];
    const [moved] = items.splice(homeDragIdx, 1);
    items.splice(dropIdx, 0, moved);

    const newOrder = items.map((item) => item.document_id);
    setHomeViewOrder(newOrder);
    setHomeDragIdx(null);
    setHomeDragOverIdx(null);

    try {
      await saveHomeViewOrder({ order: newOrder });
    } catch (err) {
      console.error("Save home order failed:", err);
    }
  };

  const handleHomeDragEnd = () => {
    setHomeDragIdx(null);
    setHomeDragOverIdx(null);
  };

  /* ── Row styles (same as feed/graph) ── */
  const homeRowClasses = (isActive) =>
    [
      "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-all duration-200 flex items-center gap-x-1.5 group/row",
      isActive
        ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
        : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
    ].join(" ");

  const renderHomeItem = (view, idx) => {
    const isDragging = homeDragIdx === idx;
    const isDragOver = homeDragOverIdx === idx;
    const isActive = view.document_id === activeHomeViewId;
    const Icon = feedViewIcon(view.icon);
    const isPendingDelete = pendingHomeDeleteId === view.document_id;

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleHomeDragStart(e, idx),
      onDragOver: (e) => handleHomeDragOver(e, idx),
      onDragLeave: handleHomeDragLeave,
      onDrop: (e) => handleHomeDrop(e, idx),
      onDragEnd: handleHomeDragEnd,
    };

    const dragStyle = {
      opacity: isDragging ? 0.35 : 1,
      borderTop:
        isDragOver && homeDragIdx !== null && homeDragIdx > idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      borderBottom:
        isDragOver && homeDragIdx !== null && homeDragIdx < idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      cursor: "grab",
    };

    return (
      <div
        key={view.document_id}
        style={dragStyle}
        {...dragProps}
        data-context-menu="home-view"
        data-context-payload={JSON.stringify(view)}
      >
        <button
          onClick={() => handleHomeViewClick(view)}
          className={homeRowClasses(isActive)}
        >
          <Icon
            className="h-3 w-3 shrink-0"
            strokeWidth={2}
            style={{ color: view.color || "rgba(14,165,233,0.70)" }}
          />
          <span className="truncate flex-1">{view.name}</span>
        </button>

        {/* ── Inline delete confirmation ── */}
        <div
          className={[
            "overflow-hidden transition-all duration-200 ease-in-out",
            isPendingDelete
              ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
              : "max-h-0 opacity-0",
          ].join(" ")}
        >
          {isPendingDelete && (
            <div className="flex items-center gap-x-2 py-1 px-1">
              <span className="text-[11px] text-slate-500 dark:text-gray-400">
                Delete?
              </span>
              <button
                onClick={() => setPendingHomeDeleteId(null)}
                className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDeleteHomeView(view.document_id)}
                className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                Confirm
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    <div className="flex flex-col gap-y-px">
      {/* + Add home view (hidden in navbar — shown in breadcrumb instead) */}
      {!isInNavbar && (
        <button
          onClick={handleAddHomeView}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors"
          title="Add home view"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>Add home view</span>
        </button>
      )}

      {/* Ordered view items */}
      {orderedHomeItems.map((view, idx) => renderHomeItem(view, idx))}
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default HomeSidebarPortal;
